import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzLinkWebComponent } from '../../../ui/gzLinkComponent';
import { GzInputWebComponent } from '../../../ui/gzInput/component/GzInputWebComponent';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { SendDownloadLinksFormWebComponent } from '../component/SendDownloadLinksFormWebComponent';
import { TemplateState } from '../../../types/common/types';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { GzChipsGeneratorInputWebComponent } from '../../../ui/gzChipsGeneratorInput';
import { CustomizableValidator } from '../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../ui/gzForm/formField/types/form-validator';
import { translateFn } from '../../../utils/translator.helper';
import { GzDropdownWebComponent } from '../../../ui/gzDropdown';
import { GzSingleSelectWebComponent } from '../../../ui/gzSingleSelectDropdown';

export class SendDownloadLinksFormView extends BaseWebComponentView {
  public component: SendDownloadLinksFormWebComponent;
  public dom: {
    $wrapper: HTMLElement;
    $formInfoField: HTMLElement;
    $loadingElement: HTMLElement;

    $addEmailSubtitle: GzTitleWebComponent;
    $installationLinksSubtitle: GzTitleWebComponent;

    $emailAddressFormField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;

    $ringSelectorFormField: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
    $ringSelectorDropDown: GzSingleSelectWebComponent;

    $windowsDownloaderFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $linuxInstallerFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $macosDownloaderFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;


    $windowsDownloaderLinkDisplay: GzLinkWebComponent;
    $windowsDownloaderLink: GzInputWebComponent;

    $linuxInstallerLinkDisplay: GzLinkWebComponent;
    $linuxInstallerLink: GzInputWebComponent;

    $macosDownloaderLinkDisplay: GzLinkWebComponent;
    $macosDownloaderLink: GzInputWebComponent;

    $sendDownloadLinksButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };
  private subscriptions: Subscription[] = [];

  getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$loadingElement = this.shadow.querySelector('#loading-element');

    this.dom.$addEmailSubtitle = this.shadow.querySelector('#add-email-subtitle');
    this.dom.$installationLinksSubtitle = this.shadow.querySelector('#installation-links-subtitle');

    this.dom.$emailAddressFormField = this.shadow.querySelector('#email-address-form-field');

    this.dom.$windowsDownloaderFormField = this.shadow.querySelector('#windows-downloader-form-field');

    this.dom.$linuxInstallerFormField = this.shadow.querySelector('#linux-installer-form-field');

    this.dom.$macosDownloaderFormField = this.shadow.querySelector('#macos-downloader-form-field');

    this.dom.$formInfoField = this.shadow.querySelector('#form-packages-info');


    this.dom.$windowsDownloaderLinkDisplay = this.shadow.querySelector('#windows-downloader-link-display');
    this.dom.$windowsDownloaderLink = this.shadow.querySelector('#windows-downloader-link');

    this.dom.$linuxInstallerLinkDisplay = this.shadow.querySelector('#linux-installer-link-display');
    this.dom.$linuxInstallerLink = this.shadow.querySelector('#linux-installer-link');

    this.dom.$macosDownloaderLinkDisplay = this.shadow.querySelector('#macos-downloader-link-display');
    this.dom.$macosDownloaderLink = this.shadow.querySelector('#macos-downloader-link');

    this.dom.$sendDownloadLinksButton = this.shadow.querySelector('#send-emails-button');
    this.dom.$cancelButton = this.shadow.querySelector('#cancel-button');

    this.dom.$ringSelectorFormField = this.shadow.querySelector('#ring-selector-formfield');
    this.dom.$ringSelectorDropDown = this.shadow.querySelector('#ring-selector-dropdown');

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  buildSuccessView(viewMetaData): void {
    this.setFormInfoVisibility();
    this.setupEmailsField();
    this.handleDownloadLinksDisplay(viewMetaData);
    this.removeLoadingState();
    this.setupFormValidations();
  }

  private setFormInfoVisibility(): void {
    toggleVisibility(this.dom.$formInfoField, this.dependencies.isPartner());
  }

  private handleDownloadLinksDisplay(linkKits): void {
    linkKits.forEach(el => {
      toggleVisibility(el.field, el.enabled);
    });
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  private setupEmailsField(): void {
    this.dom.$emailAddressFormField.Input.strings.label = translateFn('Email address');
    this.dom.$emailAddressFormField.Input.strings.placeholder = translateFn('Enter email address');
    this.dom.$emailAddressFormField.Input.validationRegex =
      '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$';
    this.dom.$emailAddressFormField.Input.strings.invalidItemErrorMessage = translateFn(
      'The email addresses provided must be valid, unique, and separated by a comma.'
    );
    this.dom.$emailAddressFormField.Input.strings.limitReachedErrorMessage = translateFn(
      'You have reached the limit of 100 email addresses.'
    );
  }

  private setupFormValidations(): void {
    this.subscriptions.push(
      this.dom.$emailAddressFormField.value$.pipe(debounceTime(250), distinctUntilChanged()).subscribe({
        next: () => {
          this.setValidators();
        },
      })
    );
  }

  private setValidators(): void {
    const emailValidators = [CustomizableValidator(this.validateEmailsSet.bind(this))];
    this.dom.$emailAddressFormField.Validators = emailValidators;
  }

  private validateEmailsSet(value: Array<any>): ValidationResult {
    if (value.length === 0) {
      return {
        valid: false,
        error: translateFn(''),
      };
    }

    return {
      valid: true,
    };
  }

  protected onDisconnect(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  setTranslatedStrings(): void {

    this.dom.$ringSelectorFormField.label = translateFn("Select ring");

    this.dom.$addEmailSubtitle.innerHTML = translateFn('Add email recipients');
    this.dom.$installationLinksSubtitle.innerHTML = translateFn('Installation links');

    this.dom.$windowsDownloaderFormField.label = translateFn('Windows Downloader');
    this.dom.$linuxInstallerFormField.label = translateFn('Linux Downloader');
    this.dom.$macosDownloaderFormField.label = translateFn('macOS Downloader');

    this.dom.$formInfoField.innerText = translateFn(
      'These packages work only for the target company.  Do not use these installation packages for other companies.'
    );

    this.dom.$sendDownloadLinksButton.innerText = translateFn('Send');
    this.dom.$cancelButton.innerText = this.dependencies.translateFn('Cancel');
  }
}
